import React, { useMemo, useState } from 'react'

import AddUser from './AddUser'
import PreviousRemindersModal from './PreviousRemindersModal'
import SendReminderModal from './SendReminderModal'
import UserTableCard from './UserTableCard'
import ViewUser from './ViewUser'
import { notifyMethod } from '../../../App'
import { useJobMasking } from '../../../contexts/JobMaskingContext'
import useTranslations from '../../../hooks/useTranslations'
import ANTDButton from '../../../shared/antd/ANTDButton'
import ANTDColumn from '../../../shared/antd/ANTDColumn'
import { ANTDSearch } from '../../../shared/antd/ANTDInput'
import ANTDModal from '../../../shared/antd/ANTDModal'
import ANTDRow from '../../../shared/antd/ANTDRow'
import ANTDSelect from '../../../shared/antd/ANTDSelect'
import ANTDTable from '../../../shared/antd/ANTDTable'
import Label from '../../../shared/Label'
import { childUsers, userWiseRole } from '../../../utils/constant'
import { include, ternary } from '../../../utils/javascript'
import userColumns from '../container/userColumn'
import userTable from '../container/userTable.container'
import { resolveUserReminderApi, sendUserReminderApi } from '../user.api'

function UserTable({
  isSearch = false,
  userData,
  customerInfo,
  permission,
  handleTableChange,
  handleDAssociate,
  className,
  rowClassNameFn = null,
  payload,
  handleSelect = null,
  multiSelect = null,
  pagination = true,
  isBuilding,
  title,
  selectedList,
  onSelect,
  removeEditBtn,
  apiCall,
  searchByEmail,
  isCardView,
  showSelectedColumns,
  showSendReminder,
  omitSearchRelationType,
  searchParamName,
}) {
  const masked = useJobMasking()
  const [resolvingReminderId, setResolvingReminderId] = useState(null)
  const {
    viewModel,
    isDesktop,
    searchResult,
    handleView,
    handleCancel,
    handleEdit,
    selectedUsers,
    handleSelectChange,
    onSearch,
    handleSearchTableChange,
    editInfo,
    setEditInfo,
    handleCancelEdit,
    searchByProps,
    reminderModel,
    reminderLoader,
    handleSendReminder,
    handleCancelReminder,
    setReminderLoader,
    previousReminderModel,
    previousReminderData,
    handlePreviousReminders,
    handlePreviousReminderTableChange,
    handleCancelPreviousReminders,
  } = userTable({
    payload,
    multiSelect,
    isBuilding,
    searchByEmail,
    omitSearchRelationType,
    searchParamName,
  })

  const handleReminderSubmit = async values => {
    setReminderLoader(true)
    try {
      const response = await sendUserReminderApi({ payload: values })

      if (response?.data) {
        notifyMethod.success({ message: 'user_reminderSentSuccess' })
        handleCancelReminder()
      } else {
        notifyMethod.error({
          message:
            response?.error?.error?.errorMsg || 'user_reminderSentFailure',
        })
      }
    } catch (error) {
      notifyMethod.error({
        message:
          error?.response?.data?.error?.errorMsg || 'user_reminderSentFailure',
      })
    } finally {
      setReminderLoader(false)
    }
  }

  const handleResolveReminder = async rowData => {
    if (!rowData?.id) return

    setResolvingReminderId(rowData?.id)
    try {
      const response = await resolveUserReminderApi({ id: rowData?.id })

      if (response?.data) {
        notifyMethod.success({ message: 'user_reminderResolvedSuccess' })
        handlePreviousReminderTableChange({
          current: previousReminderData?.pageNo || 1,
        })
      } else {
        notifyMethod.error({
          message:
            response?.error?.error?.errorMsg || 'user_reminderResolvedFailure',
        })
      }
    } catch (error) {
      notifyMethod.error({
        message:
          error?.response?.data?.error?.errorMsg ||
          'user_reminderResolvedFailure',
      })
    } finally {
      setResolvingReminderId(null)
    }
  }

  const { column, actionButtons, cardViewFn } = userColumns({
    permission,
    handleView,
    roleId: payload?.roleId,
    selectedUsers: selectedList || selectedUsers,
    handleSelectChange: ternary(
      handleSelect,
      handleSelectChange,
      onSelect || null,
    ),
    handleEdit,
    isBuilding,
    handleDAssociate,
    removeEditBtn,
    showSelectedColumns,
    showSendReminder,
    handleSendReminder,
    handlePreviousReminders,
  })
  const { dealer, collectionCenter } = userWiseRole
  const { t } = useTranslations()

  if (isSearch && searchResult.data) {
    userData = searchResult.data
    handleTableChange = handleSearchTableChange
  }

  const { loader, list } = userData || { loader: false, list: [] }
  const pageSize = ternary(isBuilding, 10, 5)

  const viewUserFN = useMemo(
    () =>
      viewModel?.open && (
        <ViewUser
          {...{
            open: viewModel?.open,
            userDetails: viewModel?.userDetails,
            handleCancel,
          }}
        />
      ),
    [viewModel?.open],
  )
  if (masked)
    return (
      <ANTDTable
        className={className}
        columns={column.filter(
          col => !['action', 'txt_Action'].includes(col.key),
        )}
        dataSource={list || []}
        pagination={false}
      />
    )

  return (
    <div className={className}>
      {ternary(
        handleSelect,
        <div className="text-end">
          <ANTDButton
            className="submit-btn mb-10"
            type="primary"
            onClick={() => handleSelect(selectedUsers)}
          >
            {t('msg_OK')}
          </ANTDButton>
        </div>,
        null,
      )}
      {title && <h3 style={{ marginBottom: '12px' }}>{t(title)}</h3>}
      <>
        {ternary(
          isSearch,
          include([dealer, collectionCenter], payload?.roleId) ? (
            <ANTDRow gutter={10} className="mt-5">
              <ANTDColumn md={12} lg={12} xs={24}>
                <Label text={t('job_SearchBy')} />
                <ANTDSelect className="w-100 mt-5" {...searchByProps} />
              </ANTDColumn>
              <ANTDColumn md={12} lg={12} xs={24}>
                <Label text={t('txt_Search')} />
                <ANTDSearch
                  className="w-100 mt-5"
                  placeholder={t(searchByProps?.value?.placeHolder)}
                  onChange={onSearch}
                />
              </ANTDColumn>
            </ANTDRow>
          ) : (
            <ANTDSearch
              className="w-100"
              placeholder={t(
                ternary(
                  searchByEmail,
                  'user_SearchEmail',
                  ternary(
                    include(childUsers, payload?.roleId),
                    'user_Name',
                    'user_BusinessName',
                  ),
                ),
              )}
              onChange={onSearch}
            />
          ),
          null,
        )}
      </>
      <div className="mt-10 user-table-card">
        {ternary(
          !isCardView && isDesktop,
          <ANTDTable
            rowClassName={rowClassNameFn && (record => rowClassNameFn(record))}
            loading={loader || searchResult.loader}
            columns={column}
            dataSource={
              list?.map(val => ({
                ...val,
                key: val?.id,
              })) || []
            }
            pagination={
              pagination
                ? {
                    hideOnSinglePage: true,
                    lastFetched: userData?.pageNo,
                    current: userData?.pageNo,
                    pageSize: pageSize,
                    total: userData?.lastPage * pageSize,
                    responsive: true,
                  }
                : false
            }
            onChange={handleTableChange}
            className={!showSelectedColumns && 'user-table'}
          />,
          <UserTableCard
            data={userData}
            actionButtons={actionButtons}
            cardViewFn={cardViewFn}
            pageSize={pageSize}
            onChange={handleTableChange}
            loader={loader}
            handleSelectChange={ternary(
              handleSelect,
              handleSelectChange,
              onSelect || null,
            )}
            selectedUsers={selectedList || selectedUsers}
          />,
        )}
      </div>
      {viewUserFN}
      {editInfo?.flag && (
        <ANTDModal
          title={t('btn_Edit')}
          centered
          open={editInfo?.flag}
          onCancel={handleCancelEdit}
          footer={false}
          width={1000}
        >
          <AddUser
            {...{
              editInfo,
              setEditInfo,
              handleCancelEdit,
              isBuilding,
              userDetails: customerInfo,
              apiCall,
            }}
          />
        </ANTDModal>
      )}
      {reminderModel?.open && (
        <SendReminderModal
          open={reminderModel?.open}
          userDetails={reminderModel?.userDetails}
          loading={reminderLoader}
          onCancel={handleCancelReminder}
          onSubmit={handleReminderSubmit}
        />
      )}
      {previousReminderModel?.open && (
        <PreviousRemindersModal
          open={previousReminderModel?.open}
          userDetails={previousReminderModel?.userDetails}
          reminderData={previousReminderData}
          onCancel={handleCancelPreviousReminders}
          onResolve={handleResolveReminder}
          resolvingId={resolvingReminderId}
          onTableChange={handlePreviousReminderTableChange}
        />
      )}
    </div>
  )
}

export default UserTable
